import {
  AutocompleteArrayInput,
  AutocompleteInput,
  ReferenceArrayInput,
  ReferenceInput,
  TextInput,
  email,
  required,
} from 'react-admin';

import { MAX_NUMBER_OF_ENTITIES, NUMBER_OF_ROWS_FOR_TEXTAREA } from '@app/constants';
import { useFormLanguage } from '@app/hooks/useFormLanguage';
import { genericfilterToQuery } from '@app/utils/filterUtils';
import { DEFAULT_LANGUAGE } from '@app/utils/languages';
import { Grid, Typography } from '@mui/material';

import { LabelNotClickableBoolInput } from '@app/components/LabelNotClickableBoolInput';
// import { RichTextInput } from 'ra-input-rich-text';
import { ImageSelector } from '@app/components/common/ImageSelector';
import { LanguageSelector } from '@app/components/common/LanguageSelector';
import { MultiLingualField } from '@app/components/common/MultiLingualField';
import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

interface ExpertFormProps {
  title: string;
  expertLogo: File | null | undefined;
  handleChangeExpertLogo(logo: File): void;
}

export const ExpertForm = ({ expertLogo, title, handleChangeExpertLogo }: ExpertFormProps) => {
  const { language, handleChangeLanguage } = useFormLanguage();

  return (
    <div>
      <Typography data-cy={`edit-${title}`} variant="h3" sx={{ marginBottom: 2 }}>
        {title}
      </Typography>
      <LanguageSelector language={language} handleChangeLanguage={handleChangeLanguage} />

      <Grid spacing={2} xl={8} lg={8} md={12} xs={12} container item>
        <Grid item xs={12}>
          <ImageSelector
            getImageId={(record: GenericObject) => record?.expert_photo_file_base_id as string}
            getImagePath={(record: GenericObject) => (record?.expert_photo_file_base as GenericObject)?.path as string}
            image={expertLogo}
            handleChangeImage={handleChangeExpertLogo}
          />
        </Grid>

        <Grid item xs={6}>
          <MultiLingualField
            Component={TextInput}
            language={language}
            source="first_name"
            label="First Name"
            helperText={false}
            variant="outlined"
            fullWidth
            validate={[required()]}
          />
        </Grid>
        <Grid item xs={6}>
          <MultiLingualField
            Component={TextInput}
            language={language}
            variant="outlined"
            helperText={false}
            label="Last Name"
            source="last_name"
            validate={[required()]}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MultiLingualField
            helperText={false}
            Component={TextInput}
            language={language}
            variant="outlined"
            label="Role"
            source="expert_roles"
            validate={[required()]}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MultiLingualField
            variant="outlined"
            helperText={false}
            Component={TextInput}
            language={language}
            multiline
            rows={NUMBER_OF_ROWS_FOR_TEXTAREA}
            fullWidth
            label="About"
            source="about"
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            variant="outlined"
            label="Email Address"
            helperText={false}
            source="email"
            validate={[email()]}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput variant="outlined" helperText={false} label="Twitter" source="twitterurl" fullWidth />
        </Grid>
        <Grid item xs={12}>
          <TextInput variant="outlined" helperText={false} label="Linkedin" source="linked_inurl" fullWidth />
        </Grid>
        <Grid item xs={12}>
          <ReferenceInput
            source="company_id"
            reference="companies"
            perPage={MAX_NUMBER_OF_ENTITIES}
            sort={{ field: 'short_name', order: 'ASC' }}>
            <AutocompleteInput
              label="Company"
              variant="outlined"
              helperText={false}
              fullWidth
              optionText={(record) => record.name[DEFAULT_LANGUAGE]}
              filterToQuery={genericfilterToQuery}
            />
          </ReferenceInput>
        </Grid>
        <Grid item xs={12}>
          <ReferenceArrayInput source="expertise_ids" reference="taxonomies" perPage={MAX_NUMBER_OF_ENTITIES}>
            <AutocompleteArrayInput
              variant="outlined"
              autoHighlight
              helperText={false}
              label="Expertise"
              filterToQuery={genericfilterToQuery}
              fullWidth
              optionText={(record) => (record?.category_name ? record?.category_name[DEFAULT_LANGUAGE] : '')}
            />
          </ReferenceArrayInput>
        </Grid>

        <Grid item xs={12}>
          <LabelNotClickableBoolInput label="Published" source="enabled" />
        </Grid>
      </Grid>
    </div>
  );
};
